import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { QuizBreadcrumb } from "@/components/QuizBreadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Zap, Users, Brain, ArrowRight, Sparkles, Clock, Trophy, Target, ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

interface FeatureItem {
  text: string;
  available: boolean;
}

interface BenefitCard {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface ProConItem {
  text: string;
  source?: string;
  sourceUrl?: string;
}

interface RelatedTool {
  name: string;
  path: string;
}

interface ComparisonPageProps {
  platformName: string;
  platformShortName?: string;
  platformUrl?: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
  heroDescription: string;
  alternativeDescription: string;
  background: string;
  pros: ProConItem[];
  cons: ProConItem[];
  cswFeatures: FeatureItem[];
  competitorFeatures: FeatureItem[];
  benefits: BenefitCard[];
  forStudentsText: string;
  pricingText: string;
  competitorPrice?: string;
  relatedTools?: RelatedTool[];
}

const allTools: RelatedTool[] = [
  { name: "Quizlet", path: "/quizzes/csw-vs-quizlet/" },
  { name: "Google Forms", path: "/quizzes/csw-vs-google-forms/" },
  { name: "FlexiQuiz", path: "/quizzes/csw-vs-flexiquiz/" },
  { name: "Quiz Maker", path: "/quizzes/csw-vs-quiz-maker/" },
  { name: "Wooclap", path: "/quizzes/csw-vs-wooclap/" },
  { name: "Genially", path: "/quizzes/csw-vs-genially/" },
  { name: "ClassMarker", path: "/quizzes/csw-vs-classmarker/" },
  { name: "Canva Quiz", path: "/quizzes/csw-vs-canva/" },
  { name: "Revisely", path: "/quizzes/csw-vs-revisely/" },
  { name: "Thea Study", path: "/quizzes/csw-vs-thea/" },
  { name: "Quiz Me!", path: "/quizzes/csw-vs-quiz-me/" },
  { name: "QuizWhiz", path: "/quizzes/csw-vs-quizwhiz/" },
  { name: "Studiosity", path: "/quizzes/csw-vs-studiosity/" },
  { name: "Test Prep Review", path: "/quizzes/csw-vs-test-prep-review/" },
  { name: "Testudy", path: "/quizzes/csw-vs-testudy/" },
];

export const ComparisonPageTemplate = ({
  platformName,
  platformShortName,
  platformUrl,
  seoTitle,
  seoDescription,
  seoKeywords,
  canonicalUrl,
  heroDescription,
  alternativeDescription,
  background,
  pros,
  cons,
  cswFeatures,
  competitorFeatures,
  benefits,
  forStudentsText,
  pricingText,
  competitorPrice,
}: ComparisonPageProps) => {
  const displayName = platformShortName || platformName;
  
  // Filter out current tool from related tools
  const relatedTools = allTools.filter(tool => 
    !canonicalUrl.toLowerCase().includes(tool.path.toLowerCase().replace(/\//g, '').replace('quizzescsw-vs-', ''))
  );
  
  // Generate JSON-LD structured data for comparison pages
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": canonicalUrl,
    "keywords": "quizzes, online study room, study stream, study verse, study room, study with strangers",
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "CSW",
      "alternateName": "Chill Study Work",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "url": "https://csw.live",
      "description": "Free online study platform with quizzes, flashcards, study rooms, and productivity tools for students worldwide.",
      "featureList": [
        "AI-powered quiz generation",
        "Flashcard creation",
        "Virtual study rooms",
        "Study stream with video",
        "Pomodoro timer",
        "Task management",
        "Study with strangers",
        "Group study sessions"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "ratingCount": "2500",
        "bestRating": "5",
        "worstRating": "1"
      },
      "author": {
        "@type": "Organization",
        "name": "CSW",
        "url": "https://csw.live"
      }
    },
    "about": {
      "@type": "Thing",
      "name": `Comparison between CSW and ${platformName}`,
      "description": heroDescription
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": platformName,
      "url": platformUrl || undefined,
      "applicationCategory": "EducationalApplication"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      },
      "sameAs": [
        "https://www.instagram.com/csw.live/",
        "https://www.youtube.com/@CSW.live.workspace"
      ]
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://app.csw.live",
      "name": "Try CSW Free"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <Header />
      
      <main className="container mx-auto px-4 pt-28 pb-16">
        <QuizBreadcrumb platformName={platformName} />
        
        {/* Hero Section - Visual Heavy */}
        <section className="relative mb-20 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 rounded-3xl" />
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-accent/10 rounded-full blur-[100px]" />
          
          <div className="relative z-10 text-center py-16 px-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Free Alternative</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-6">
              <span className="text-muted-foreground">CSW vs </span>
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                {displayName}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-10">
              {heroDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              {platformUrl && (
                <Button size="lg" variant="outline" className="font-semibold px-8 py-6 text-lg" asChild>
                  <a href={platformUrl} target="_blank" rel="noopener noreferrer">
                    Visit {displayName}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
            
            {/* Stats Row */}
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary">100%</p>
                <p className="text-sm text-muted-foreground">Free Forever</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary">AI</p>
                <p className="text-sm text-muted-foreground">Powered Quizzes</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary">24/7</p>
                <p className="text-sm text-muted-foreground">Study Rooms</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary">0</p>
                <p className="text-sm text-muted-foreground">Ads or Limits</p>
              </div>
            </div>
          </div>
        </section>

        {/* Video/Demo Placeholder Section */}
        <section className="mb-20">
          <div className="relative max-w-5xl mx-auto">
            <div className="aspect-video rounded-2xl overflow-hidden border border-border/50 shadow-2xl bg-gradient-to-br from-muted/50 to-muted">
              <video 
                src="/videos/productivity-demo.mp4" 
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-lg font-semibold text-foreground mb-2">See CSW in Action</p>
                <p className="text-sm text-muted-foreground">Experience the future of studying</p>
              </div>
            </div>
          </div>
        </section>

        {/* Background Section */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-center">
              What is <span className="text-primary">{displayName}</span>?
            </h2>
            <Card className="border border-border/50 bg-muted/20">
              <CardContent className="p-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {background}
                </p>
                {platformUrl && (
                  <a 
                    href={platformUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-primary hover:text-primary/80 transition-colors"
                  >
                    Visit {platformName} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pros and Cons Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-center mb-4">
            {displayName} <span className="text-primary">Pros & Cons</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            An honest look at what {displayName} offers and its limitations
          </p>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Pros Card */}
            <Card className="border-2 border-green-500/30 bg-green-500/5">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <ThumbsUp className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-green-500">Pros</h3>
                </div>
                <ul className="space-y-4">
                  {pros.map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green-500/20 mt-0.5 shrink-0">
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                      <div>
                        <span className="text-foreground">{pro.text}</span>
                        {pro.source && pro.sourceUrl && (
                          <a 
                            href={pro.sourceUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="ml-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            ({pro.source})
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Cons Card */}
            <Card className="border-2 border-red-500/30 bg-red-500/5">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <ThumbsDown className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-red-500">Cons</h3>
                </div>
                <ul className="space-y-4">
                  {cons.map((con, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-red-500/20 mt-0.5 shrink-0">
                        <X className="w-4 h-4 text-red-500" />
                      </div>
                      <div>
                        <span className="text-foreground">{con.text}</span>
                        {con.source && con.sourceUrl && (
                          <a 
                            href={con.sourceUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="ml-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            ({con.source})
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Alternative Section */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6">
              The Best <span className="text-primary">{displayName} Alternative</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              {alternativeDescription}
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Switch to CSW — It's Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

        {/* Feature Comparison - Visual Cards */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-center mb-4">
            Feature Comparison
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            See how CSW stacks up against {displayName}
          </p>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* CSW Card */}
            <Card className="relative overflow-hidden border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-sm font-semibold rounded-bl-lg">
                RECOMMENDED
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-primary">CSW</h3>
                    <p className="text-sm text-muted-foreground">Free Forever</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {cswFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${feature.available ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {feature.available ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}
                      </div>
                      <span className="text-foreground">{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-8 bg-primary hover:bg-primary/90" size="lg" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Get Started Free
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Competitor Card */}
            <Card className="border border-border/50 bg-muted/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                    <Target className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-muted-foreground">{displayName}</h3>
                    {competitorPrice && <p className="text-sm text-muted-foreground">{competitorPrice}</p>}
                  </div>
                </div>
                <ul className="space-y-4">
                  {competitorFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${feature.available ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {feature.available ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}
                      </div>
                      <span className={feature.available ? 'text-foreground' : 'text-muted-foreground'}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-center mb-4">
            Why Students Choose <span className="text-primary">CSW</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            Over {displayName}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="group hover:border-primary/50 transition-all hover:shadow-lg hover:shadow-primary/5">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* For Students Section */}
        <section className="mb-20">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-muted/50 to-muted p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px]" />
            <div className="relative z-10 max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
                CSW vs {displayName} for <span className="text-primary">Students</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {forStudentsText}
              </p>
              <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Join Free as a Student
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6">
              CSW vs {displayName} <span className="text-primary">Pricing</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {pricingText}
            </p>
            
            {/* Pricing Visual */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-2 border-primary/30 bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold mb-2">CSW</h3>
                  <p className="text-5xl font-bold text-primary mb-2">$0</p>
                  <p className="text-muted-foreground">Forever Free</p>
                </CardContent>
              </Card>
              <Card className="border border-border/50">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold mb-2 text-muted-foreground">{displayName}</h3>
                  <p className="text-5xl font-bold text-muted-foreground mb-2">{competitorPrice || '$$$'}</p>
                  <p className="text-muted-foreground">Per Year</p>
                </CardContent>
              </Card>
            </div>
            
            <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Studying Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

        {/* Related Comparisons Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            Compare Other <span className="text-primary">Quiz Tools</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            See how CSW compares to other popular study and quiz platforms
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {relatedTools.slice(0, 10).map((tool) => (
              <Link
                key={tool.path}
                to={tool.path}
                className="px-4 py-2 rounded-full border border-border/50 bg-muted/30 hover:bg-primary/10 hover:border-primary/30 transition-all text-sm font-medium"
              >
                CSW vs {tool.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 p-12 md:p-16">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-[120px]" />
          
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
              <Trophy className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Join 10,000+ Students</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6">
              Ready to Switch from {displayName}?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of students who've discovered a better way to study with CSW. It's completely free — no credit card required.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Now — It's Free
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
